import React from "react";
import { useForm } from "react-hook-form";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { Button, Text, CheckBox, TextArea, Img, SelectBox, Input, Heading } from "../../components";
import metadata from "libphonenumber-js/metadata.full.json";
import ModalProvider from "react-modal";

export default function ContactForm({ isOpen, onRequestClose, ...props }) {
  // Set up form with default values
  const { register, handleSubmit, reset, formState: { errors } } = useForm({
    defaultValues: {
      name: "",
      email: "",
      phone: "",
      message: "",
    }
  });

  const countryOptions = React.useMemo(() => {
    return Object.entries(metadata.countries).map(([code, data]) => {
      const callingCode = data[0];
      return {
        value: code,
        label: (
          <div className="flex items-center gap-2">
            <img
              src={`https://catamphetamine.github.io/country-flag-icons/3x2/${code}.svg`}
              alt={code}
              className="h-4 w-6"
            />
            <span className="text-gray-900">{code}</span>
            <span className="text-gray-500">(+{callingCode})</span>
          </div>
        ),
      };
    });
  }, []);

  // Form submission handler
  const onSubmitContactForm = async (data) => {
    data.subject = "ZFLOC web support";

    try {
      const res = await fetch("https://eoh8usamft74p5c.m.pipedream.net", {
        method: "POST",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
        },
        body: JSON.stringify(data),
      });

      if (res.status === 200) {
        reset();
        toast.success("Message sent successfully!");
      } else {
        toast.error("Something went wrong!");
      }
    } catch (e) {
      console.error(e);
      toast.error("Something went wrong!");
    }
  };

  return (
    <ModalProvider
      {...props}
      appElement={document.getElementById("root")}
      isOpen={isOpen}
      onRequestClose={onRequestClose}
      className="fixed inset-0 z-50 sm:z-50 flex items-center justify-center bg-gray-950 bg-opacity-80 overflow-y-auto pt-[20px]"
    >
      <div className="w-full max-w-5xl px-4 md:px-8 py-8 z-50">
        <div className="relative z-50 flex flex-col 2xl:flex-row  xl:flex-row lg:flex-row bg-white-a700 rounded-[40px] shadow-lg max-h-screen overflow-y-auto">
          <button
            onClick={onRequestClose}
            className="absolute top-5 right-5 flex h-10 w-10 items-center justify-center rounded-full bg-transparent hover:bg-gray-200 focus:outline-none"
          >
            <span className="sr-only">Close</span>
            <svg
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              strokeWidth="1.5"
              stroke="currentColor"
              className="h-6 w-6 text-gray-600"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>

          <div className="flex-shrink-0 w-full 2xl:w-1/2 2xl:h-auto xl:w-1/2 xl:h-auto lg:w-1/2 h-[40vh] lg:h-auto max-h-[50vh] 2xl:max-h-full xl:max-h-full lg:max-h-full md:hidden sm:hidden">
            <Img
              src="images/contact-form.svg"
              alt="Contact Form"
              className="h-full w-full rounded-[40px] object-cover"
            />
          </div>

          <div className="flex flex-col w-full lg:w-1/2 p-[60px] overflow-y-auto">
            <Heading
              size="heading2xl"
              as="h1"
              className="text-[30px] font-medium uppercase text-gray-900"
            >
              Let's Level Up Your Brand Together!
            </Heading>
            <form onSubmit={handleSubmit(onSubmitContactForm)} className="mt-4 space-y-4">
              <div>
                <Heading as="p" className="text-sm font-medium text-gray-700">Name</Heading>
                <Input
                  shape="round"
                  type="text"
                  name="name"
                  placeholder="Your name"
                  className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2"
                  {...register("name", { required: "Name is required" })}
                />
                {errors.name && <Text className="text-red-500 text-xs md:text-sm sm:text-sm">{errors.name.message}</Text>}
              </div>
              <div>
                <Heading as="p" className="text-sm font-medium text-gray-700">Email</Heading>
                <Input
                  shape="round"
                  type="email"
                  name="email"
                  placeholder="you@company.com"
                  className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2"
                  {...register("email", { required: "Email is required", pattern: /^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}$/ })}
                />
                {errors.email && <Text className="text-red-500 text-xs md:text-sm sm:text-sm">{errors.email.message}</Text>}
              </div>
              <div>
                <Heading as="p" className="text-sm font-medium text-gray-700">Phone number</Heading>
                <div className="mt-1 flex gap-2">
                  <SelectBox
                    options={countryOptions}
                    defaultValue={countryOptions.find((option) => option.value === "US")}
                    className="w-1/4 px-2 py-2 border border-solid border-gray-300 rounded-lg"
                  />
                  <input
                    name="phone"
                    placeholder="Your phone number"
                    type="tel"
                    className="w-full rounded-lg border border-gray-300 px-3 py-2"
                    {...register("phone", { required: "Phone number is required" })}
                  />
                </div>
                {errors.phone && <Text className="text-red-500 text-xs md:text-sm sm:text-sm">{errors.phone.message}</Text>}
              </div>
              <div>
                <Heading as="p" className="text-sm font-medium text-gray-700">How can we help?</Heading>
                <TextArea
                  shape="round"
                  name="message"
                  placeholder="Tell us a little about the project..."
                  className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 min-h-10 max-h-60"
                  {...register("message", { required: "Message is required" })}
                />
                {errors.message && <Text className="text-red-500 text-xs md:text-sm sm:text-sm">{errors.message.message}</Text>}
              </div>
              <Button
                size="5xl"
                type="submit"
                className="mt-6 w-full rounded-lg bg-gray-900 py-3 text-white-a700 hover:bg-gray-700"
              >
                Get Started
              </Button>
            </form>
            <ToastContainer />
          </div>
        </div>
      </div>
    </ModalProvider>
  );
}